/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { usePageContentId } from '@confluence/page-context';
import { type ConfluenceCommentResolveAllLocation } from '@confluence/comments-panel-queries/entry-points/ResolveAllCommentsMutation';
import {
	type ResolvedCommentsQueryType,
	type ResolvedCommentsQueryVariables,
} from '@confluence/comments-panel-queries/entry-points/ResolvedCommentsQuery';
import { ResolvedCommentsQuery } from '@confluence/comments-panel-queries/entry-points/ResolvedCommentsQuery.graphql';
import { type GraphQLContentStatus } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';

import type {
	useCommentBulkResolveResolveAllCommentsMutation as useCommentBulkResolveResolveAllCommentsMutation$data,
	useCommentBulkResolveResolveAllCommentsMutationVariables as useCommentBulkResolveResolveAllCommentsMutation$variables,
} from './__types__/useCommentBulkResolveResolveAllCommentsMutation';
import { useCommentBulkOperationsActions } from './useCommentBulkOperations';
import { useSyncResolvedComments } from './useSyncResolvedComments';
import type { Flags } from './useCommentBulkOperationsFlags';
import { useLocation } from './useLocation';

const useCommentBulkResolveResolveAllCommentsMutation = gql`
	mutation useCommentBulkResolveResolveAllCommentsMutation(
		$contentId: ID!
		$cloudId: ID!
		$resolveView: ConfluenceCommentResolveAllLocation = RENDERER
	) {
		confluence_resolveCommentsByContentId(
			contentId: $contentId
			cloudId: $cloudId
			resolveView: $resolveView
		) {
			success
			errors {
				message
			}
		}
	}
`;
export const useCommentBulkResolve = (flags: Flags) => {
	const [contentId = ''] = usePageContentId();
	const { cloudId } = useSessionData();
	const location = useLocation();
	const { setIsBulkResolveInProgress } = useCommentBulkOperationsActions();
	const syncResolvedComments = useSyncResolvedComments(contentId);

	const handleResolveAllComments = () => {
		void resolveAllComments({
			variables: {
				contentId,
				cloudId,
				resolveView: location as ConfluenceCommentResolveAllLocation,
			},
		});

		setIsBulkResolveInProgress(true);
	};

	const [resolveAllComments] = useMutation<
		useCommentBulkResolveResolveAllCommentsMutation$data,
		useCommentBulkResolveResolveAllCommentsMutation$variables
	>(useCommentBulkResolveResolveAllCommentsMutation, {
		onCompleted: async ({ confluence_resolveCommentsByContentId }) => {
			const client = getApolloClient();
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			const { data: resolvedCommentsData } = await client.query<
				ResolvedCommentsQueryType,
				ResolvedCommentsQueryVariables
			>({
				query: ResolvedCommentsQuery,
				variables: {
					pageId: contentId,
					contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
				},
				fetchPolicy: 'network-only',
			});
			syncResolvedComments(resolvedCommentsData);
			if (confluence_resolveCommentsByContentId?.success) {
				flags.showSuccess();
			} else {
				flags.showFailure(handleResolveAllComments);
			}
			setIsBulkResolveInProgress(false);
		},
		onError: () => {
			flags.showFailure(handleResolveAllComments);
			setIsBulkResolveInProgress(false);
		},
	});
	return handleResolveAllComments;
};
